import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Button,
  Chip,
  alpha,
  useTheme,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Building2,
  Users,
  FileText,
  TrendingUp,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Ban,
  CheckCircle2,
} from "lucide-react";
import {
  fetchMarketingOrganizations,
  deleteMarketingOrganization,
} from "../../../../../../redux/actions/marketingAction";
import { enqueueSnackbar } from "notistack";
import DeleteConfirmDialog from "../DeleteConfirmDialog";
import OrganizationFiche from "./OrganizationFiche/OrganizationFiche";

const STATUS_COLORS = {
  lead: "#6b7280", testing: "#3b82f6", demo_booked: "#8b5cf6",
  proposal: "#f59e0b", closed: "#10b981", lost: "#ef4444",
};
const STATUS_LABELS = {
  lead: "Lead", testing: "Testing", demo_booked: "Demo Booked",
  proposal: "Proposal", closed: "Closed", lost: "Lost",
};

const MarketingOrgList = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const { organizations, loading } = useSelector((state) => state.marketing);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [ficheOrgId, setFicheOrgId] = useState(null);
  const [scoreSort, setScoreSort] = useState(null); // null | "desc" | "asc"
  const [activeFilter, setActiveFilter] = useState("all"); // "all" | "active" | "inactive"

  useEffect(() => {
    const params = { page: page + 1, page_size: 25 };
    if (search) params.search = search;
    if (scoreSort) params.sort_by = "score";
    if (scoreSort) params.sort_order = scoreSort;
    if (activeFilter === "active") params.is_active = true;
    if (activeFilter === "inactive") params.is_active = false;
    dispatch(fetchMarketingOrganizations(params));
  }, [dispatch, page, search, scoreSort, activeFilter]);

  const items = organizations?.items || [];
  const total = organizations?.total || 0;

  const headerSx = {
    fontWeight: 600,
    fontSize: "0.75rem",
    textTransform: "uppercase",
    color: theme.palette.text.secondary,
    bgcolor: theme.palette.mode === "dark" ? "#1a1a1a" : "#f5f5f5",
  };

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Toolbar */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2.5 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: "1rem" }}>
          {t("mkt_organisations")} ({total})
        </Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<Plus size={16} />}
          onClick={() => navigate("/admin/marketing/organisations/new")}
          sx={{ textTransform: "none", borderRadius: "8px", fontWeight: 600 }}
        >
          {t("mkt_new_organisation")}
        </Button>
      </Box>

      <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
        <TextField
          size="small"
          placeholder={t("mkt_search_organisations")}
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          sx={{ width: 280 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={16} color={theme.palette.text.secondary} />
              </InputAdornment>
            ),
          }}
        />
        <FormControl size="small" sx={{ width: 200 }}>
          <InputLabel>{t("mkt_activation")}</InputLabel>
          <Select
            value={activeFilter}
            onChange={(e) => { setActiveFilter(e.target.value); setPage(0); }}
            label={t("mkt_activation")}
          >
            <MenuItem value="all">{t("mkt_filter_orgs_all")}</MenuItem>
            <MenuItem value="active">{t("mkt_filter_orgs_active_only")}</MenuItem>
            <MenuItem value="inactive">{t("mkt_filter_orgs_inactive_only")}</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Paper
        elevation={0}
        sx={{
          borderRadius: "12px",
          border: `1px solid ${theme.palette.divider}`,
          overflow: "hidden",
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress size={32} />
          </Box>
        ) : (
          <TableContainer sx={{ flex: 1, overflow: "auto" }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={headerSx}>{t("mkt_organisation")}</TableCell>
                  <TableCell sx={headerSx}>Status</TableCell>
                  <TableCell sx={headerSx} align="right">Valeur</TableCell>
                  <TableCell
                    sx={{ ...headerSx, cursor: "pointer", userSelect: "none", "&:hover": { color: theme.palette.primary.main } }}
                    align="center"
                    onClick={() => {
                      setScoreSort((prev) => prev === null ? "desc" : prev === "desc" ? "asc" : null);
                      setPage(0);
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
                      Score
                      {scoreSort === null && <ArrowUpDown size={13} style={{ opacity: 0.4 }} />}
                      {scoreSort === "desc" && <ArrowDown size={13} />}
                      {scoreSort === "asc" && <ArrowUp size={13} />}
                    </Box>
                  </TableCell>
                  <TableCell sx={headerSx}>{t("mkt_contacts")}</TableCell>
                  <TableCell sx={headerSx} align="center">{t("mkt_activation")}</TableCell>
                  <TableCell sx={headerSx}>{t("common_date")}</TableCell>
                  <TableCell sx={headerSx} align="right">{t("mkt_actions")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
                        <Building2 size={40} strokeWidth={1} color={theme.palette.text.disabled} />
                        <Typography variant="body2" color="text.secondary">
                          {t("mkt_no_organisations")}
                        </Typography>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<Plus size={14} />}
                          onClick={() => navigate("/admin/marketing/organisations/new")}
                          sx={{ mt: 1, textTransform: "none" }}
                        >
                          {t("mkt_create_organisation")}
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((org) => {
                    const statusColor = STATUS_COLORS[org.status] || "#6b7280";
                    const statusLabel = STATUS_LABELS[org.status] || "Lead";
                    const isActive = org.is_active !== false;
                    return (
                    <TableRow
                      key={org.id}
                      hover
                      sx={{
                        cursor: "pointer",
                        opacity: isActive ? 1 : 0.6,
                        "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.04) },
                      }}
                      onClick={() => setFicheOrgId(org.id)}
                    >
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Box
                            sx={{
                              width: 32, height: 32, borderRadius: "8px",
                              bgcolor: alpha(theme.palette.primary.main, 0.1),
                              display: "flex", alignItems: "center", justifyContent: "center",
                            }}
                          >
                            <Building2 size={16} color={theme.palette.primary.main} />
                          </Box>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{org.name}</Typography>
                            {org.city && (
                              <Typography variant="caption" color="text.secondary">{org.city}</Typography>
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={statusLabel} size="small"
                          sx={{
                            height: 22, fontSize: "0.65rem", fontWeight: 600, borderRadius: "6px",
                            bgcolor: alpha(statusColor, 0.1), color: statusColor,
                          }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ fontSize: "0.8rem", fontWeight: org.estimated_value ? 600 : 400, color: org.estimated_value ? theme.palette.text.primary : theme.palette.text.disabled }}>
                          {org.estimated_value ? `${Number(org.estimated_value).toLocaleString()} ${org.currency || "MAD"}` : "—"}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        {org.score > 0 ? (
                          <Chip icon={<TrendingUp size={10} />} label={org.score} size="small"
                            sx={{ height: 20, fontSize: "0.6rem", fontWeight: 700, bgcolor: alpha("#10b981", 0.1), color: "#10b981", "& .MuiChip-icon": { color: "#10b981" } }}
                          />
                        ) : "—"}
                      </TableCell>
                      <TableCell>
                        <Chip icon={<Users size={12} />} label={org.contact_count || 0} size="small" sx={{ fontSize: "0.75rem", height: 22 }} />
                      </TableCell>
                      <TableCell align="center">
                        {isActive ? (
                          <Chip
                            icon={<CheckCircle2 size={11} />}
                            label={t("mkt_active")}
                            size="small"
                            sx={{
                              height: 20, fontSize: "0.65rem", fontWeight: 600,
                              bgcolor: alpha("#10b981", 0.1), color: "#10b981",
                              "& .MuiChip-icon": { color: "#10b981" },
                            }}
                          />
                        ) : (
                          <Chip
                            icon={<Ban size={11} />}
                            label={t("mkt_deactivated")}
                            size="small"
                            sx={{
                              height: 20, fontSize: "0.65rem", fontWeight: 600,
                              bgcolor: alpha("#ef4444", 0.1), color: "#ef4444",
                              "& .MuiChip-icon": { color: "#ef4444" },
                            }}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {org.created_at
                            ? new Date(org.created_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" })
                            : "—"}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                        <Box sx={{ display: "flex", gap: 0.5, justifyContent: "flex-end" }}>
                          <Tooltip title={t("common_edit")}>
                            <IconButton size="small" onClick={() => navigate(`/admin/marketing/organisations/${org.id}`)}>
                              <Edit size={15} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t("common_delete")}>
                            <IconButton size="small" onClick={() => setDeleteTarget(org)} sx={{ color: "error.main" }}>
                              <Trash2 size={15} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {total > 25 && (
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={25}
            rowsPerPageOptions={[25]}
          />
        )}
      </Paper>

      <DeleteConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={async () => {
          try {
            await dispatch(deleteMarketingOrganization(deleteTarget.id));
            enqueueSnackbar(t("mkt_org_deleted"), { variant: "success" });
          } catch (err) {
            enqueueSnackbar(err.message || "Erreur", { variant: "error" });
          }
          setDeleteTarget(null);
        }}
        itemName={deleteTarget?.name}
        itemType={t("mkt_organisation")}
      />

      <OrganizationFiche
        open={Boolean(ficheOrgId)}
        onClose={() => setFicheOrgId(null)}
        orgId={ficheOrgId}
      />
    </Box>
  );
};

export default MarketingOrgList;
